import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AppointmentInfo } from '../../ui-module/appointment-info.js';
import { PhoneLink } from '../../ui-module/phone-link.js';
import { useTenantConfig } from '../../tenant-config-module/use-tenant-config.js';
import { BusinessErrorReason } from '../state-machine-feature/business-error-reason.js';
import type { AppointmentInfo as AppointmentInfoData } from '../../token-module/appointment-info.interface.js';

interface BusinessErrorScreenProps {
  readonly reason: BusinessErrorReason;
  readonly appointmentInfo?: AppointmentInfoData;
}

interface ReasonKeys {
  readonly titleKey: string;
  readonly bodyKey: string;
}

const REASON_KEY_MAP: Readonly<Record<BusinessErrorReason, ReasonKeys>> = {
  [BusinessErrorReason.TokenExpired]: {
    titleKey: 'businessError.reasons.TOKEN_EXPIRED.title',
    bodyKey: 'businessError.reasons.TOKEN_EXPIRED.body',
  },
  [BusinessErrorReason.TokenAlreadyUsed]: {
    titleKey: 'businessError.reasons.TOKEN_ALREADY_USED.title',
    bodyKey: 'businessError.reasons.TOKEN_ALREADY_USED.body',
  },
  [BusinessErrorReason.PracticeRejected]: {
    titleKey: 'businessError.reasons.PRACTICE_REJECTED.title',
    bodyKey: 'businessError.reasons.PRACTICE_REJECTED.body',
  },
  [BusinessErrorReason.TooLate]: {
    titleKey: 'businessError.reasons.TOO_LATE.title',
    bodyKey: 'businessError.reasons.TOO_LATE.body',
  },
};

const ICON_POP = {
  initial: { scale: 0.6, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  transition: { type: 'spring' as const, stiffness: 300, damping: 18 },
};

/**
 * Business-Error-Screen mit zwei Optik-Pfaden:
 *  - `TOO_LATE` rendert das Lovable-Notice-Layout: rotgetönte Karte mit
 *    Body-Text und Tap-to-Call-CTA, **ohne** Termininfo und ohne X-Icon
 *    (der Termin liegt zu nah, das Display einer Termininfo wäre
 *    irreführend).
 *  - Alle anderen Reasons rendern das Standard-Error-Layout: Termininfo,
 *    64-px-Kreis mit X, Headline und Body, Telefon-CTA darunter.
 *
 * A11y: `role="alert"` + `aria-live="assertive"` auf der Fehlernachricht.
 * `data-reason` erlaubt deterministisches Targeting in Tests und Telemetrie.
 */
export function BusinessErrorScreen(props: BusinessErrorScreenProps): JSX.Element {
  if (props.reason === BusinessErrorReason.TooLate) {
    return <NoticeLayout reason={props.reason} />;
  }
  return (
    <StandardLayout
      reason={props.reason}
      {...(props.appointmentInfo ? { appointmentInfo: props.appointmentInfo } : {})}
    />
  );
}

function NoticeLayout(props: { readonly reason: BusinessErrorReason }): JSX.Element {
  const { t } = useTranslation();
  const config = useTenantConfig();
  const { titleKey, bodyKey } = REASON_KEY_MAP[props.reason];
  return (
    <div className="flex flex-col items-center gap-6">
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t(titleKey)}</h1>
      <div className="w-full rounded-2xl border border-error/20 bg-error/5 px-5 py-6 text-center">
        <p
          role="status"
          aria-live="assertive"
          className="text-sm leading-relaxed text-error"
          data-reason={props.reason}
        >
          {t(bodyKey)}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">{config.phoneNumber}</p>
      </div>
      <PhoneLink phoneNumber={config.phoneNumber} label={t('businessError.callLabel')} />
    </div>
  );
}

function StandardLayout(props: {
  readonly reason: BusinessErrorReason;
  readonly appointmentInfo?: AppointmentInfoData;
}): JSX.Element {
  const { t } = useTranslation();
  const config = useTenantConfig();
  const { titleKey, bodyKey } = REASON_KEY_MAP[props.reason];
  return (
    <div className="flex flex-col items-center gap-6">
      <AppointmentInfo
        headlineKey={titleKey}
        {...(props.appointmentInfo ? { info: props.appointmentInfo } : {})}
      />
      <motion.div
        className="flex h-16 w-16 items-center justify-center rounded-full bg-error/15"
        initial={ICON_POP.initial}
        animate={ICON_POP.animate}
        transition={ICON_POP.transition}
        aria-label={t('businessError.iconAlt')}
        role="img"
      >
        <X className="h-8 w-8 text-error" strokeWidth={3} />
      </motion.div>
      <p
        role="status"
        aria-live="assertive"
        className="max-w-xs text-center text-sm leading-relaxed text-error"
        data-reason={props.reason}
      >
        {t(bodyKey)}
      </p>
      <PhoneLink phoneNumber={config.phoneNumber} label={t('businessError.callLabel')} />
    </div>
  );
}
